import { useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MagnifyingGlass, Plus, Stack, SquaresFour, X, Cube,
} from '@phosphor-icons/react';
import { apiFormaleta, pareceCodigo } from '@/api/formaleta';
import type { ModeloFormaleta } from '@/api/formaleta';
import { Aviso, Chip } from '@/components/formaleta/comunes';
import { TarjetaModelo } from '@/components/formaleta/TarjetaModelo';
import { TarjetaPieza } from '@/components/formaleta/TarjetaPieza';
import { PanelAlta } from '@/components/formaleta/PanelAlta';

// Inventario de formaleta de aluminio — la pantalla de CAMPO.
//
// Alcance cerrado con Daniel el 21-oct-2026:
//   ¿Para quién?       «las dos, pero arrancamos por la de campo»
//   ¿Con qué?          «solo el inventario, pero muy superior al de Power Apps
//                       (fotos, búsqueda, filtros, totales)»
//
// Dos funciones, las dos de campo:
//   1. CONSULTAR  — dos modos sobre el mismo buscador: `Modelos` (agrupado) y
//      `Todas las piezas` (una por una). Tocar un modelo lo despliega.
//   2. INVENTARIAR — el alta, que es un flujo de hallazgo: «encontré esta pieza
//      y la estoy inventariando aquí mismo».
//
// La canvas app de Power Apps es el PISO FUNCIONAL, no la referencia estética.
// Daniel: «ese era el alcance que da Power Apps, que es muy limitado, muy
// básico, pero tengo toda la intención de superarla en todo sentido».
//
// Lo que esta pantalla NO puede responder todavía, y lo dice en pantalla en vez
// de disimularlo:
//   · «¿cuál es la más cercana?» — de las 353 tablas de AdelanteDB la única con
//     latitud/longitud era `fotos`. Solo habrá GPS de las piezas que se muevan
//     desde ahora: no hay histórico que rellenar.
//   · «¿dónde están?» — se responde por código de obra, porque
//     `FormMovimiento` tiene 0 filas: la logística nunca existió.
//   · «¿cuándo se lavó?» — `FechaUltLavado` nace vacía.

type Modo = 'modelos' | 'piezas';

export function FormaletaPage() {
  const [modo, setModo] = useState<Modo>('modelos');
  const [q, setQ] = useState('');
  const [idCategoria, setIdCategoria] = useState<number | null>(null);
  const [sinCategoria, setSinCategoria] = useState(false);
  const [idEstado, setIdEstado] = useState<number | null>(null);
  const [soloDisponibles, setSoloDisponibles] = useState(false);
  // `undefined` = cerrado · `null` = abierto en alta de modelo nuevo ·
  // objeto = abierto sobre un modelo/renglón concreto.
  //
  // Arranca en `null` —o sea, el formulario ABIERTO en modelo nuevo— y no en
  // `undefined`. Es la vista por defecto desde el rediseño del 5-nov-2026:
  // mañana arranca el inventario físico y lo que se hace cientos de veces es
  // dar de alta piezas, no explorar el catálogo. Cerrar el panel deja la
  // exploración de modelos detrás, que es el caso raro.
  const [alta, setAlta] = useState<{ modelo: ModeloFormaleta; idForm?: number } | null | undefined>(null);

  const { data: cat, isLoading: cargandoCat, error: errorCat } = useQuery({
    queryKey: ['formaleta', 'catalogos'],
    queryFn: () => apiFormaleta.catalogos(),
  });

  const filtro = { q, idCategoria, sinCategoria };

  const { data: modelos, isLoading: cargandoModelos } = useQuery({
    queryKey: ['formaleta', 'modelos', filtro],
    queryFn: () => apiFormaleta.modelos(filtro),
    enabled: modo === 'modelos',
  });

  const { data: pagina, isLoading: cargandoPiezas } = useQuery({
    queryKey: ['formaleta', 'piezas', filtro, idEstado, soloDisponibles],
    queryFn: () => apiFormaleta.piezas({ ...filtro, idEstado, soloDisponibles }),
    enabled: modo === 'piezas',
  });

  // Un consecutivo tecleado o escaneado se resuelve directo, sin obligar a
  // cambiar de pestaña: en campo lo que se tiene a mano es la pieza marcada.
  const esCodigo = pareceCodigo(q);
  const { data: escaneada } = useQuery({
    queryKey: ['formaleta', 'codigo', q],
    queryFn: () => apiFormaleta.porCodigo(q),
    enabled: esCodigo && !!cat?.piezasHabilitadas,
    retry: false,
  });

  if (errorCat) {
    return (
      <div className="h-full overflow-y-auto p-6">
        <Aviso
          tono="alerta"
          titulo="El inventario de formaleta no contestó"
          detalle={(errorCat as Error).message}
          pista="Si dice «Invalid object name 'FormInventario'», la base apuntada no es AdelanteDB."
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* ── Cabecera con los totales ── */}
      <header className="shrink-0 border-b border-brand-gray-100 bg-white px-4 pt-4">
        <div className="mb-3 flex items-center gap-2">
          <Cube size={20} weight="bold" className="text-brand-gray-500" />
          <h1 className="text-xl font-semibold text-brand-gray-500">Formaleta</h1>
          <span className="rounded-full bg-brand-gray-100 px-2 py-0.5 text-[11px] font-medium text-brand-gray-400">
            inventario de campo
          </span>
        </div>

        {cat && (
          <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1">
            <Total n={cat.totales.modelos} rotulo="modelos" />
            <Total n={cat.totales.renglones} rotulo="renglones" />
            <Total n={cat.totales.piezasDeclaradas} rotulo="piezas declaradas" />
            {/* Las dos cifras NUNCA se colapsan en una: la brecha ES la deuda
                de inventariado. */}
            <Total
              n={cat.piezasHabilitadas ? cat.totales.piezasInventariadas : null}
              rotulo="inventariadas una por una"
              alerta={cat.piezasHabilitadas && cat.totales.piezasInventariadas === 0}
            />
            <Total n={cat.totales.renglonesSinFoto} rotulo="renglones sin foto"
              alerta={cat.totales.renglonesSinFoto > 0} />
          </div>
        )}

        {/* ── Buscador: el uso del día a día ── */}
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-brand-gray-200 px-3">
          <MagnifyingGlass size={18} weight="bold" className="text-brand-gray-300" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Buscar pieza, código o consecutivo (AD-00412)"
            className="flex-1 py-3 text-sm text-brand-gray-500 placeholder:text-brand-gray-300 focus:outline-none"
          />
          {q && (
            <button type="button" onClick={() => setQ('')}
              className="rounded p-1 text-brand-gray-300 hover:text-brand-gray-500">
              <X size={16} weight="bold" />
            </button>
          )}
        </div>

        {/* ── Los dos modos ── */}
        <div className="flex gap-2 pb-3">
          <Pestana
            icono={<SquaresFour size={15} weight="bold" />}
            texto="Modelos" activo={modo === 'modelos'} onClick={() => setModo('modelos')} />
          <Pestana
            icono={<Stack size={15} weight="bold" />}
            texto="Todas las piezas" activo={modo === 'piezas'} onClick={() => setModo('piezas')} />
        </div>
      </header>

      {/* ── Filtros ── */}
      {cat && (
        <div className="shrink-0 border-b border-brand-gray-100 bg-white">
          <div className="flex gap-2 overflow-x-auto px-4 py-2.5">
            <Chip texto="Todas" conteo={cat.totales.renglones}
              activo={!idCategoria && !sinCategoria}
              onClick={() => { setIdCategoria(null); setSinCategoria(false); }} />
            {cat.categorias.map(c => (
              <Chip
                key={c.id}
                texto={c.nombre || `#${c.id}`}
                conteo={c.renglones}
                colorHex={c.colorHex}
                activo={idCategoria === c.id}
                onClick={() => { setIdCategoria(c.id); setSinCategoria(false); }}
              />
            ))}
            {/* El chip que la app de Power Apps no tiene y es el más grande de
                todos: 184 renglones y ≈17.600 piezas sin categoría — los
                accesorios, el 93% de las piezas. Esconderlos haría que la
                pantalla muestre el 7% del inventario. */}
            {cat.totales.renglonesSinCategoria > 0 && (
              <Chip
                texto="Sin categoría"
                conteo={cat.totales.renglonesSinCategoria}
                activo={sinCategoria}
                onClick={() => { setSinCategoria(v => !v); setIdCategoria(null); }}
              />
            )}
          </div>

          {modo === 'piezas' && cat.estados.length > 0 && (
            <div className="flex gap-2 overflow-x-auto border-t border-brand-gray-100 px-4 py-2.5">
              <Chip texto="Cualquier estado" activo={!idEstado && !soloDisponibles}
                onClick={() => { setIdEstado(null); setSoloDisponibles(false); }} />
              <Chip texto="Solo disponibles" activo={soloDisponibles}
                onClick={() => { setSoloDisponibles(v => !v); setIdEstado(null); }} />
              {cat.estados.map(e => (
                <Chip key={e.id} texto={e.estado} colorHex={e.colorHex}
                  activo={idEstado === e.id}
                  onClick={() => { setIdEstado(e.id); setSoloDisponibles(false); }} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── La lista ── */}
      <div className="flex-1 overflow-y-auto bg-brand-bg p-4">
        {cargandoCat && <p className="text-sm text-brand-gray-400">Abriendo el inventario…</p>}

        {cat && !cat.piezasHabilitadas && (
          <div className="mb-4">
            <Aviso
              tono="alerta"
              titulo="El nivel de PIEZA todavía no existe en la base"
              detalle={cat.motivoDeshabilitado}
              pista="Hasta que corra, la pantalla muestra lo que sí existe: los 509 renglones comprados y su cantidad declarada. El consecutivo, el QR, el estado y el paradero por pieza quedan a la espera."
            />
          </div>
        )}

        {escaneada && (
          <section className="mb-4">
            <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-brand-gray-400">
              Pieza encontrada por código
            </p>
            <TarjetaPieza pieza={escaneada} />
          </section>
        )}

        {modo === 'modelos' && (
          <>
            {cargandoModelos && <p className="text-sm text-brand-gray-400">Buscando modelos…</p>}
            {!cargandoModelos && modelos && modelos.length === 0 && (
              <Aviso
                titulo="Nada con esos filtros"
                detalle={q ? `No hay modelos que coincidan con «${q}».` : undefined}
                pista="Probá con el código del fabricante (CR-90X120), con la categoría, o quitá el filtro."
              />
            )}
            <div className="space-y-2.5">
              {(modelos || []).map(m => (
                <TarjetaModelo
                  key={m.nombre}
                  modelo={m}
                  piezasHabilitadas={!!cat?.piezasHabilitadas}
                  motivo={cat?.motivoDeshabilitado ?? null}
                  onInventariar={(modelo, idForm) => setAlta({ modelo, idForm })}
                />
              ))}
            </div>
          </>
        )}

        {modo === 'piezas' && (
          <>
            {cargandoPiezas && <p className="text-sm text-brand-gray-400">Buscando piezas…</p>}

            {pagina && !pagina.piezasHabilitadas && (
              <Aviso
                tono="alerta"
                titulo="Todavía no hay piezas individuales"
                detalle={pagina.motivo}
                pista="Este modo es el que Daniel pidió para el día a día: buscar una pieza puntual y ver si hay alguna disponible. Se enciende con la migración."
              />
            )}

            {pagina?.piezasHabilitadas && pagina.piezas.length === 0 && (
              <Aviso
                titulo="Ninguna pieza inventariada con esos filtros"
                detalle={`Las tablas responden. Hay ${cat?.totales.piezasDeclaradas ?? 0} piezas declaradas por la compra y ${cat?.totales.piezasInventariadas ?? 0} identificadas una por una.`}
                pista="No hay backfill a propósito: generar las 18.961 de golpe crearía 18.961 QR que nadie pegó. Entran por «Inventariar», a medida que se encuentran."
              />
            )}

            <div className="space-y-2.5">
              {(pagina?.piezas || []).map(p => <TarjetaPieza key={p.id} pieza={p} />)}
            </div>

            {pagina && pagina.total > pagina.mostradas && (
              <p className="mt-4 text-center text-xs text-brand-gray-400">
                Mostrando {pagina.mostradas} de {pagina.total}. Afiná la búsqueda
                o el filtro para ver el resto.
              </p>
            )}
          </>
        )}
      </div>

      {/* ── Inventariar ── */}
      <button
        type="button"
        onClick={() => setAlta(null)}
        className="absolute bottom-5 right-5 flex items-center gap-2 rounded-full bg-brand-green-100 px-5 py-4 text-sm font-bold text-black shadow-lg hover:bg-brand-green-200"
      >
        <Plus size={18} weight="bold" /> Inventariar
      </button>

      {alta !== undefined && cat && (
        <PanelAlta
          catalogos={cat}
          inicial={alta}
          onCerrar={() => setAlta(undefined)}
        />
      )}
    </div>
  );
}

function Total({ n, rotulo, alerta }: { n: number | null; rotulo: string; alerta?: boolean }) {
  return (
    <div>
      <p className={`font-mono text-lg font-bold leading-none ${
        alerta ? 'text-brand-red-200' : 'text-brand-gray-500'
      }`}>
        {n === null ? '—' : n.toLocaleString('es-CR')}
      </p>
      <p className="mt-0.5 text-[10px] uppercase tracking-wide text-brand-gray-400">{rotulo}</p>
    </div>
  );
}

function Pestana({ icono, texto, activo, onClick }: {
  icono: ReactNode;
  texto: string;
  activo: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-t-lg border-b-2 px-3 py-2 text-sm font-semibold ${
        activo
          ? 'border-brand-green-100 text-brand-gray-500'
          : 'border-transparent text-brand-gray-400 hover:text-brand-gray-500'
      }`}
    >
      {icono}
      {texto}
    </button>
  );
}
